import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconClock, IconRefreshAlert } from '@tabler/icons-react';
import { formatDate } from './mealPlanUtils';

interface UsageLimitError {
  error: string;
  operation?: string;
  reason?: string;
  limit?: number;
  currentUsage?: number;
  resetDate?: string;
  remainingCount?: number;
  // For meal plan substitution limits
  aiSubstitutionsUsed?: number;
  maxAiSubstitutions?: number;
  alternativeOptions?: string[];
}

/**
 * Show a user-friendly notification when a 429 usage limit is exceeded
 */
export const showUsageLimitNotification = (errorData: UsageLimitError) => {
  const { operation, reason, resetDate, alternativeOptions } = errorData;

  // Determine notification content based on operation type
  let title = 'Usage limit reached';
  let message = reason || 'You have reached your usage limit for this feature.';
  let color = 'orange';
  let icon = IconAlertTriangle;

  if (operation === 'meal_plan_generation') {
    title = 'Meal plan limit reached';
    message = "You've reached your monthly limit of meal plans.";
    if (resetDate) {
      message += ` Your limit will reset on ${formatDate(resetDate)}.`;
    }
    icon = IconClock;
  } else if (operation === 'recipe_import_image' || operation === 'recipe_import_url') {
    title = 'Recipe import limit reached';
    message = "You've reached your monthly limit of recipe imports.";
    if (resetDate) {
      message += ` Your limit will reset on ${formatDate(resetDate)}.`;
    }
    icon = IconClock;
  } else if (errorData.alternativeOptions && alternativeOptions) {
    title = 'Swap limit reached';
    message = "You’ve used all the new-recipe swaps for this meal plan. Try one of your own recipes instead.";
    icon = IconRefreshAlert;
  }

  notifications.show({
    title,
    message,
    color,
    icon: icon === IconAlertTriangle ? <IconAlertTriangle size={18} /> : 
          icon === IconClock ? <IconClock size={18} /> : 
          <IconRefreshAlert size={18} />,
    autoClose: 10000,
  });
};

/**
 * Check if an error response is a 429 usage limit error and handle it
 * Returns true if it was a usage limit error (and notification was shown)
 */
export const handleUsageLimitError = (response: Response, errorData: any): boolean => {
  if (response.status === 429 && errorData) {
    showUsageLimitNotification(errorData);
    return true;
  }
  return false;
};
